<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: AncestorTree                                          |
| Section: views/trees                                             |
| Purpose: Ancestor tree route for parent-line exploration.        |
+------------------------------------------------------------------+
-->
<template>
    <div class="surname-tree-container">
        <div id="zoom-buttons">
            <button @click="zoomIn"><img src="@/assets/icons/zoom_in_red.svg" alt="Zoom In"></button>
            <button @click="zoomOut"><img src="@/assets/icons/zoom_out_red.svg" alt="Zoom Out"></button>
        </div>

        <div class="ancestor-list-container">
            <div class="ancestor-list-header">
                <p>{{ $t('treeViews.ancestorsOfCount', { count: uniqueAncestors, name: probandusName }) }}</p>
                <a class="current-person" :href="localePath(`/person/${personId}`)">{{ probandusName }}</a>
            </div>
            <div v-for="item in filteredAncestorList" :key="item.type === 'label' ? `label-${item.depth}` : item.id">
                <!-- Render generation labels as collapsible panels -->
                <CollapsiblePanel v-if="item.type === 'label'" :label="item.label" arrowColor="var(--brand-color-1)"
                    :isAncestorTree="true">
                    <!-- Render ancestor items inside the collapsible panel -->
                    <div v-for="ancestor in filteredAncestorList.filter(a => a.type === 'ancestor' && a.depth === item.depth)"
                        :key="ancestor.id" class="ancestor-item"
                        :class="{ highlighted: ancestor.id === selectedAncestor }"
                        @click="!ancestor.placeholder && selectAncestor(ancestor.id, false)">
                        {{ ancestor.name }}

                        <!-- Secondary label for the selected person -->
                        <div v-if="!ancestor.placeholder && ancestor.id === selectedAncestor && selectedPersonDetails" class="secondary-label">
                            <button @click.stop="goToSearch(ancestor.id)">
                                {{ $t('treeViews.viewProfile') }}
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17"
                                    fill="var(--brand-color-1)">
                                    <path
                                        d="M15.7063 9.20627C16.0969 8.81565 16.0969 8.18127 15.7063 7.79065L11.7063 3.79065C11.3156 3.40002 10.6812 3.40002 10.2906 3.79065C9.9 4.18127 9.9 4.81565 10.2906 5.20627L12.5844 7.50002H1C0.446875 7.50002 0 7.9469 0 8.50002C0 9.05315 0.446875 9.50002 1 9.50002H12.5844L10.2906 11.7938C9.9 12.1844 9.9 12.8188 10.2906 13.2094C10.6812 13.6 11.3156 13.6 11.7063 13.2094L15.7063 9.2094V9.20627Z"
                                        fill="var(--brand-color-1)" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </CollapsiblePanel>
            </div>
        </div>

        <div id="surname-tree-container"></div>
    </div>
</template>

<script>
import * as d3 from "d3";
import { useAuth } from '@/composables/useAuth';
import { getAncestors } from '@/api/personService';
import CollapsiblePanel from '@/components/CollapsiblePanel.vue';
import { useLocale } from '@/composables/useLocale';
import { debugLog } from '@/utils/logger';
import { getPersonNodeKey, isLivingPlaceholder, isPrivateLivingPerson } from '@/utils/personPrivacy';

export default {
    name: 'AncestorTree',
    components: {
        CollapsiblePanel,
    },
    props: {
        personId: {
            type: String,
            required: true
        }
    },
    setup() {
        // Injecting properties from App.vue using the useAuth composable
        const { authToken, username, logout } = useAuth();
        const { localePath } = useLocale();

        return {
            authToken,
            username,
            logout,
            localePath,
        };
    },
    data() {
        return {
            // inputPersonID: 'I43',        // Default Person ID for testing
            selectedGenerations: 10,    // Matches the backend recursion safety limit.
            zoom: null,                 // D3 zoom behavior
            treeData: null,
            ancestorList: [],           // List of ancestors
            uniqueAncestors: 0,         // Count unique ancestors
            selectedAncestor: "",       // Currently selected ancestor
            dropdownOpen: null,         // Dropdown menu state
            probandusName: "",          // Name of the probandus (root person)
            selectedPersonDetails: null, // Details of the selected person
        };
    },
    mounted() {
        this.fetchAncestorGraph();
        window.addEventListener('resize', this.resizeTree);
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.resizeTree);
    },
    methods: {
        async fetchAncestorGraph() {
            try {
                const resp = await getAncestors(this.personId, this.selectedGenerations); // Use personId from props

                if (!resp || !resp.personID) {
                    throw new Error("Invalid API response: Missing root person data.");
                }

                // Store the probandus name
                this.probandusName = `${resp.firstname || this.$t('treeViews.unknown')} ${resp.lastname || this.$t('treeViews.unknown')}`;

                const nodes = [];
                const links = [];
                const visited = new Set();

                // Recursive function to flatten nested ancestors and assign depth
                const flatten = (person, depth = 0) => {
                    const personNodeID = getPersonNodeKey(person);
                    if (!person || !personNodeID || visited.has(personNodeID)) return;
                    visited.add(personNodeID);
                    const placeholder = isLivingPlaceholder(person);
                    const isPrivate = isPrivateLivingPerson(person, this.authToken);

                    nodes.push({
                        id: personNodeID,
                        name: placeholder
                            ? this.$t('privacy.livingPlaceholder')
                            : isPrivate ? this.$t('nav.privacyHidden') : `${person.firstname} ${person.lastname}`,
                        photo: person.photoPath || null,
                        sex: person.sex,
                        private: isPrivate,
                        placeholder,
                        depth, // Add depth property
                    });

                    if (person.ancestors && person.ancestors.length > 0) {
                        person.ancestors.forEach((ancestor) => {
                            links.push({
                                source: personNodeID,
                                target: getPersonNodeKey(ancestor),
                            });
                            flatten(ancestor, depth + 1); // Increment depth for the next generation
                        });
                    }
                };

                flatten(resp); // Start from the root person
                this.treeData = { nodes, links };

                // Predefined generation labels
                const generationLabels = [
                    this.$t('treeViews.generationLabels.probandus'),
                    this.$t('treeViews.generationLabels.parents'),
                    this.$t('treeViews.generationLabels.grandparents'),
                    this.$t('treeViews.generationLabels.greatGrandparents'),
                    this.$t('treeViews.generationLabels.secondGreatGrandparents'),
                    this.$t('treeViews.generationN', { n: 5 }),
                    this.$t('treeViews.generationN', { n: 6 }),
                    this.$t('treeViews.generationN', { n: 7 }),
                    this.$t('treeViews.generationN', { n: 8 }),
                    this.$t('treeViews.generationN', { n: 9 }),
                    this.$t('treeViews.generationN', { n: 10 }),
                    this.$t('treeViews.generationN', { n: 11 }),
                    this.$t('treeViews.generationN', { n: 12 }),
                    this.$t('treeViews.generationN', { n: 13 }),
                    this.$t('treeViews.generationN', { n: 14 }),
                    this.$t('treeViews.generationN', { n: 15 }),
                    this.$t('treeViews.generationN', { n: 16 }),
                ];

                // Group ancestors by depth and prepare the list with labels
                const groupedAncestors = [];
                nodes.sort((a, b) => a.depth - b.depth).forEach((node) => {
                    // Add a label for the generation if it doesn't already exist
                    if (!groupedAncestors.some(item => item.type === "label" && item.depth === node.depth)) {
                        groupedAncestors.push({
                            type: "label",
                            depth: node.depth,
                            label: generationLabels[node.depth] || this.$t('treeViews.generationN', { n: node.depth }), // Fallback label
                        });
                    }
                    // Add the ancestor node
                    groupedAncestors.push({
                        type: "ancestor",
                        ...node,
                    });
                });

                this.ancestorList = groupedAncestors;
                this.uniqueAncestors = nodes.length;

                d3.select("#surname-tree-container").selectAll("*").remove();
                this.createSurnameTree(this.treeData);
            } catch (err) {
                console.error("Error fetching ancestor graph:", err);
                alert(this.$t('treeViews.ancestorsLoadError'));
            }
        },

        goToSearch(personID) {
            // reuse the same router‐nav logic
            this.$router.push(this.localePath(`/person/${encodeURIComponent(personID)}`));
        },

        async selectAncestor(id) {
            if (this.treeData?.nodes?.find(node => node.id === id)?.placeholder) return;
            if (this.selectedAncestor === id) {
                this.selectedAncestor = "";
                this.selectedPersonDetails = null; // Clear details
                this.highlightAncestors(); // Clear highlights
            } else {
                this.selectedAncestor = id;
                this.highlightAncestors(); // Highlight the selected ancestor
                this.centerOnNode(id); // Optionally center the view on the selected node

                // Fetch details of the selected person
                try {
                    const response = await getAncestors(id, 0); // Fetch only the selected person
                    this.selectedPersonDetails = {
                        name: `${response.firstname || this.$t('treeViews.unknown')} ${response.lastname || this.$t('treeViews.unknown')}`,
                        birthDate: response.birthdatetr || this.$t('treeViews.unknown'),
                        deathDate: response.deathdatetr || this.$t('treeViews.unknown'),
                        sex: response.sex || this.$t('treeViews.unknown'),
                    };
                } catch (error) {
                    console.error("Error fetching person details:", error);
                    this.selectedPersonDetails = null; // Clear details on error
                }

                // Scroll the ancestor list to the highlighted item
                const ancestorList = this.$el.querySelector(".ancestor-list-container");
                const highlightedItem = ancestorList.querySelector(".ancestor-item.highlighted");
                if (highlightedItem) {
                    const offset = 8; // Add a little offset
                    const topPosition = highlightedItem.offsetTop - ancestorList.offsetTop - offset;
                    ancestorList.scrollTo({ top: topPosition, behavior: "smooth" });
                }
            }
        },

        highlightAncestors() {
            const svg = d3.select("#surname-tree-container").select("svg");

            if (this.selectedAncestor) {
                debugLog("Selected Ancestor:", this.selectedAncestor);

                // Find the path from the selected ancestor to the probandus
                const pathToRoot = [];
                const findPath = (nodeId, links) => {
                    let currentId = nodeId;

                    while (currentId) {
                        pathToRoot.push(currentId);
                        const parentLink = links.find(link => link.target === currentId);
                        currentId = parentLink ? parentLink.source : null;
                    }
                };

                findPath(this.selectedAncestor, this.treeData.links);
                debugLog("Path to Root:", pathToRoot);

                // Filter links to include only those in the path to root
                const pathLinks = this.treeData.links.filter(
                    link => pathToRoot.includes(link.source) && pathToRoot.includes(link.target)
                );
                debugLog("Path Links:", pathLinks);

                // Highlight the nodes in the path
                svg.selectAll(".nodes circle")
                    .classed("highlighted", d => pathToRoot.includes(d.id))
                    .classed("dimmed", d => !pathToRoot.includes(d.id))
                    .attr("fill", d => {
                        if (pathToRoot.includes(d.id)) {
                            if (d.id === this.inputPersonID) {
                                // Root node (probandus) retains its original color
                                return d.sex === "M" ? "#ae1e25" : d.sex === "F" ? "#ae1e25" : "#ae1e25"; // Blue for male, pink for female, gray for unknown
                            } else {
                                // Ancestor nodes are yellow
                                return "#FFCC33"; // Yellow for highlighted ancestor nodes
                            }
                        } else {
                            return "#DDD"; // Dimmed color for non-highlighted nodes
                        }
                    });

                // Highlight the links in the path
                svg.selectAll(".links line")
                    .classed("highlighted", d => pathLinks.some(link => link.source === d.source.id && link.target === d.target.id))
                    .classed("dimmed", d => !pathLinks.some(link => link.source === d.source.id && link.target === d.target.id));

                // Dim or highlight the labels
                svg.selectAll(".labels text")
                    .attr("fill", d => {
                        if (pathToRoot.includes(d.id)) {
                            return "#000"; // Black for highlighted labels
                        } else {
                            return "#AAA"; // Grey for dimmed labels
                        }
                    });
            } else {
                debugLog("No ancestor selected. Resetting highlights.");

                // Remove highlights if no ancestor is selected
                svg.selectAll(".nodes circle")
                    .classed("highlighted", false)
                    .classed("dimmed", false)
                    .attr("fill", d => d.sex === "M" ? "#ae1e25" : d.sex === "F" ? "#ae1e25" : "#ae1e25"); // Reset to gender-based color

                svg.selectAll(".links line")
                    .classed("highlighted", false)
                    .classed("dimmed", false);

                // Reset label colors
                svg.selectAll(".labels text")
                    .attr("fill", "#000"); // Reset to black
            }
        },

        toggleDropdown(nodeId) {
            this.dropdownOpen = this.dropdownOpen === nodeId ? null : nodeId;
        },
        closeDropdown() {
            this.dropdownOpen = null;
        },

        /**
 * Adjust SVG dimensions on container resize
     */
        resizeTree() {
            const container = d3.select('.surname-tree-container').node();
            const width = container.offsetWidth;
            const height = container.offsetHeight;
            const svg = d3.select('#surname-tree-container svg');
            if (!svg.empty()) {
                svg.attr('width', width).attr('height', height);
            }
        },

        onAncestorClick(personID) {
            this.selectedAncestor = personID;
            // reuse your zoom/center logic—e.g.:
            this.centerOnNode(personID);
        },

        centerOnNode(personID) {
            const svg = d3.select('#surname-tree-container svg');
            const node = svg.select(`.nodes circle[data-id="${personID}"]`);
            if (node.empty()) return;
            const { x, y } = node.datum();
            const container = d3.select('.surname-tree-container').node();
            const w = container.clientWidth, h = container.clientHeight;
            const scale = 1.5;
            const tx = w / 2 - x * scale, ty = h / 2 - y * scale;
            svg.transition().duration(750)
                .call(this.zoom.transform, d3.zoomIdentity.translate(tx, ty).scale(scale));
        },

        /** Zoom in the tree */
        zoomIn() {
            const svg = d3.select('#surname-tree-container svg');
            svg.transition().duration(500).call(this.zoom.scaleBy, 1.5);
        },

        /** Zoom out the tree */
        zoomOut() {
            const svg = d3.select('#surname-tree-container svg');
            svg.transition().duration(500).call(this.zoom.scaleBy, 0.5);
        },

        /** Core D3 tree creation */
        createSurnameTree(data) {
            const container = d3.select('.surname-tree-container').node();
            const width = container.offsetWidth;
            const height = container.offsetHeight;

            this.zoom = d3.zoom().on('zoom', event => {
                d3.select('#surname-tree-container g').attr('transform', event.transform);
            });

            const svg = d3.select('#surname-tree-container')
                .append('svg')
                .attr('width', width)
                .attr('height', height)
                .call(this.zoom)
                .append('g');

            // Unique nodes
            const nodes = data.nodes;

            // Prepare link objects
            const links = data.links.map(l => ({
                source: nodes.find(node => node.id === l.source),
                target: nodes.find(node => node.id === l.target)
            }));

            // Draw links
            const link = svg.append('g')
                .attr('class', 'links')
                .selectAll('line')
                .data(links)
                .enter()
                .append('line')
                .attr('stroke', '#aaa')
                .attr('stroke-width', 1);

            // Draw nodes
            const node = svg.append('g')
                .attr('class', 'nodes')
                .selectAll('circle')
                .data(nodes)
                .enter()
                .append('circle')
                .attr('data-id', d => d.id)
                .attr('r', 8)
                .attr('fill', d => d.sex === 'M' ? '#ae1e25' : d.sex === 'F' ? '#ae1e25' : '#ae1e25') // Blue for male, pink for female, gray for unknown
                .attr('stroke', '#fff')
                .attr('stroke-width', 1.5)
                .classed('dimmed', d => d.private)
                .call(d3.drag()
                    .on('start', (event, d) => {
                        if (!event.active) simulation.alphaTarget(0.3).restart();
                        d.fx = d.x; d.fy = d.y;
                    })
                    .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
                    .on('end', (event, d) => {
                        if (!event.active) simulation.alphaTarget(0);
                        d.fx = null; d.fy = null;
                    })
                )
                .on('click', (event, d) => {
                    event.stopPropagation(); // Prevent propagation to the SVG canvas
                    this.selectAncestor(d.id); // Call the method to handle selection
                });

            // Add click listener to reset highlights when clicking on the canvas
            d3.select('#surname-tree-container').select('svg')
                .on('click', () => {
                    this.selectedAncestor = ""; // Reset the selected ancestor
                    this.highlightAncestors(); // Clear all highlights
                });

            // Node labels
            svg.append('g')
                .attr('class', 'labels')
                .selectAll('text')
                .data(nodes)
                .enter()
                .append('text')
                .attr('x', 12)
                .attr('dy', '.35em')
                .style('font-size', '12px')
                .text(d => d.name);

            // Force simulation
            const simulation = d3.forceSimulation(nodes)
                .force('link', d3.forceLink(links).id(d => d.id).distance(100))
                .force('charge', d3.forceManyBody().strength(-100))
                .force('center', d3.forceCenter(width / 2, height / 2))
                .on('tick', () => {
                    link
                        .attr('x1', d => d.source.x)
                        .attr('y1', d => d.source.y)
                        .attr('x2', d => d.target.x)
                        .attr('y2', d => d.target.y);

                    node.attr('cx', d => d.x).attr('cy', d => d.y);

                    svg.selectAll('text')
                        .attr('x', d => d.x + 12)
                        .attr('y', d => d.y);
                })
                .on('end', () => {
                    this.centerOnNode(this.personId);
                });

            simulation.force('link').links(links);
        }
    },
    computed: {
        filteredAncestorList() {
            return this.ancestorList.filter(item => {
                // Exclude the probandus (type === 'ancestor' && depth === 0)
                if (item.type === 'ancestor' && item.depth === 0) {
                    return false;
                }
                // Exclude the probandus label (type === 'label' && depth === 0)
                if (item.type === 'label' && item.depth === 0) {
                    return false;
                }
                // Include all other items
                return true;
            });
        }
    },
};
</script>

<style scoped>
.surname-tree-container {
    height: 100vh !important;
}

.ancestor-list-container h1 {
    font-family: var(--font-primary);
    /* color: var(--brand-text-dark); */
    color: var(--brand-color-1);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: 125%;
    text-transform: inherit;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.ancestor-list-header {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-start;
    padding: 16px 24px;
    border-bottom: 1px solid #f0f0f0;
}

.ancestor-list-header p {
    font-size: 14px;
    font-weight: 900;
    text-transform: uppercase;
}

.ancestor-tree-container {
    position: relative;
    height: calc(100vh - 110px) !important;
    /* margin-top: -110px; */
    /* height: 100vh !important; */
    width: 100% !important;
    overflow: visible;
}

.ancestor-tree-container:hover {
    cursor: grab;
}

.ancestor-tree-container:active {
    cursor: grabbing;
}

/* #controls {
    margin-bottom: 20px;
} */

#zoom-buttons {
    position: absolute;
    bottom: 24px;
    right: 24px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 100;
}

#zoom-buttons button {
    padding: 0;
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0);
    border: none;
    border-radius: var(--border-radius-pill);
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px var(--brand-color-1);
}

#zoom-buttons button:hover {
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
}

#surname-tree-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
    /* background-color: red; */
}

.links line {
    stroke: #aaa;
    stroke-width: 1px;
}

.nodes circle {
    cursor: pointer;
    stroke: #fff;
    stroke-width: 1.5px;
}

.nodes circle:hover {
    stroke: black;
}

.labels text {
    font-family: Arial, sans-serif;
    pointer-events: none;
}

.highlighted {
    stroke: black !important;
    fill: #ae1e25;
    /* Strong color for highlighted nodes */
}

.nodes circle.ancestor-highlighted {
    fill: #FFCC33;
    /* Yellow for highlighted ancestor nodes */
}

.secondary {
    stroke: black !important;
    fill: #FFCC33 !important;
    /* Highlight secondary nodes in yellow */
}

.dimmed {
    stroke: #ccc !important;
    fill: #ddd;
    z-index: 1;
    /* Lighter color for unhighlighted nodes */
}

.links line.highlighted {
    stroke: black !important;
    z-index: 10;
    /* Strong color for highlighted links */
}

.links line.secondary {
    stroke: black !important;
    /* Yellow color for secondary links */
}

.links line.dimmed {
    stroke: #ddd !important;
    z-index: 1;
    /* Lighter color for unhighlighted links */
}

/* .surname-list-label {
    font-weight: 700;
} */

.generation-label {
    border-bottom: 1px solid #f0f0f0;
    padding: 8px 24px;
    font-weight: 700;
    /* color: var(--brand-color-1); */
    color: var(--brand-text-dark);
    font-size: 14px;
    text-transform: uppercase;
}

.generation-label strong {
    font-weight: 900;
}

.ancestor-list-container {
    /* height: calc(100% - 48px); */
    height: fit-content;
    max-height: calc(100% - 48px);
    /* overflow-y: auto; */
    overflow: auto;
    /* border: 1px solid #aaa; */
    border-radius: 16px;
    padding: 2px 0;
    background-color: #fff;
    /* width: fit-content; */
    width: 100%;
    max-width: 360px;
    position: absolute;
    top: 24px;
    left: 24px;
    z-index: 10;
    box-shadow: var(--shadow-card-lg);
}

.ancestor-item {
    position: relative;
    padding: 8px 24px 8px 48px;
    cursor: pointer;
    font-weight: 600;
    border-bottom: 1px solid #f0f0f0;
    /* display: flex; */
    /* flex-direction: row; */
    /* justify-content: space-between; */
}

.ancestor-item:hover .search-button {
    display: inline-block;
}

.search-button {
    display: none;
    /* Hidden by default */
    background-color: var(--brand-color-1);
    /* color: ; */
    border: none;
    border-radius: 4px;
    padding: 6px;
    cursor: pointer;
    /* font-size: 12px; */
    transition: background-color 0.2s ease;
    position: absolute;
    right: 12px;
    top: 6px;
    /* transform: translateY(-50%); */
}

.search-button:hover {
    background-color: #800000;
    /* Darker shade for hover */
}

.ancestor-item:hover .dropdown-toggle {
    display: inline-block;
}

.ancestor-tree-container .dropdown {
    position: absolute;
    right: 24px;
    top: 6px;
    display: inline-block;
}

.ancestor-tree-container .dropdown-toggle {
    display: none;
    background-color: var(--brand-color-1);
    border: none;
    border-radius: 4px;
    padding: 6px 9px;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.ancestor-tree-container .dropdown-toggle:hover {
    background-color: var(--brand-color-2);
}

.ancestor-tree-container .dropdown-menu {
    display: none;
    position: absolute;
    top: 100%;
    right: -12px;
    background: var(--brand-bg-1);
    box-shadow: var(--shadow-card-lg);
    border-radius: 16px;
    z-index: 1000;
    padding: 8px 0;
    overflow: hidden;
}

.ancestor-tree-container .dropdown:hover .dropdown-menu {
    display: block;
}

.ancestor-tree-container .dropdown-menu button {
    width: 100%;
    padding: 8px 24px;
    background: none;
    border: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s;
}

.ancestor-tree-container .dropdown-menu button:hover {
    background-color: #f5f5f5;
    text-decoration: underline;
}

/* .ancestor-item:last-child {
    border-bottom: none;
} */

.ancestor-item:hover {
    background-color: #f0f0f0;
    text-decoration: underline;
}

.ancestor-item.highlighted {
    background-color: #f0f0f0;
    /* Highlight color */
    /* color: white; */
    color: var(--brand-color-1);
    font-weight: bold;
    z-index: 10;
    padding: 16px 24px 16px 48px;
}

.ancestor-item.highlighted:hover {
    text-decoration: none;
}

.secondary-label,
.secondary-item {
    color: var(--brand-text-dark);
    font-weight: 400;
    font-size: 12px;
}

.secondary-label {
    margin-top: 8px;
}

.secondary-label button {
    border-radius: var(--border-radius-sm);
    border: 1px solid var(--brand-color-1);
    color: var(--brand-color-1);
    font-weight: 600;
    cursor: pointer;
    font-size: 14px;
    padding: 4px 8px;
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;
}

.secondary-label button:hover {
    text-decoration: underline;
}

.secondary-label button svg {
    stroke: none !important;
}

.secondary-list {
    /* margin-top: 8px; */
    padding-left: 0;
    list-style: none;
}

.secondary-item {
    /* margin-bottom: 4px; */
    /* cursor: pointer; */
    padding-left: 16px;
    position: relative;
}

.secondary-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 8px;
    height: 8px;
    background-color: var(--brand-color-3);
    border-radius: var(--border-radius-pill);
    border: 1px solid var(--brand-text-dark);
}

.current-person {
    font-weight: 700;
    color: var(--brand-color-1);
    font-size: 24px;
    text-decoration: none;
}

.current-person:hover {
    text-decoration: underline;
}

@media (max-width: 860px) {
    .ancestor-list-container {
        display: none;
    }
}
</style>
